import { Portal, Project } from 'common/contracts';
import { EToasterStatus } from 'utils/showToast';

export interface ILoginResponse {
  version: number;
  userId: string;
  userName: string;
  email: string;
  company: string;
  projects: Project[];
  portals: Portal[];
  zendeskToken: string;
  companyDisplayName: string;
  isOnboarding: boolean;
}

export interface LoginData {
  userName?: string;
  password?: string;
  loginToken?: string;
  accessToken?: string;
  rememberMe?: boolean;
  isZendesk?: boolean;
  returnTo?: string;
  microsoftAccount?: any; 
}

export enum ELoginAuthMethod {
  USERNAME_PASSWORD = 'USERNAME_PASSWORD',
  GOOGLE = 'GOOGLE',
  MICROSOFT = 'MICROSOFT',
  ACCESS_TOKEN= 'ACCESS_TOKEN'
}

export interface LoginNotificationData {
  message: string;
  type: EToasterStatus;
}

export enum EModalState {
  LOGIN = 'login',
  MFA_VERIFICATION = 'mfaVerification',
  LOCKOUT = 'lockout',
  RESET_PASSWORD = 'resetPassword',
  FORGET_PASSWORD = 'forgetPassword',
  FINISH_ACCOUNT = 'finishAccount'
}

export interface LoginPayload {
  userId: string;
  authJwt?: string;
  currentPublisherId?: string;
}

export interface MfaAuthenticateRequest {
  email: string;
  password: string;
  rememberDevice: boolean;
}

export interface MfaAuthenticateResponse {
  mfaEnabled: boolean;
  mfaSessionToken: string;
  maxAttempts: number;
  currentAttempt: number;
  loginResponse?: ILoginResponse;
}

export interface MfaVerifyRequest {
  mfaSessionToken: string;
  code: string;
}

export interface MfaResendRequest {
  mfaSessionToken: string;
  email: string;
}

export interface MfaResendResponse {
  nextResendAvailableAt: string;
  mfaSessionToken: string;
}

interface BaseErrorResponse {
  error: string;
  message: string;
  statusCode: number;
}

export interface MfaErrorResponse extends BaseErrorResponse {
  attemptsRemaining: number;
  maxAttempts: number;
}

export interface RateLimitErrorResponse extends BaseErrorResponse {
  lockUntil?: string;
  nextLoginAvailableAt?: string;
  nextOTPAvailableAt?: string;
}

export enum ELockoutType {
  LOGIN = 'login',
  OTP = 'otp'
}

export interface LockoutState {
  endTime: Date | null;
  type: ELockoutType | null;
}

export interface MfaSession {
  email: string;
  password: string;
  rememberMe: boolean;
  mfaSessionToken: string | null;
}

export interface MfaAttempts {
  maxAttempts: number;
  currentAttempt: number;
}

export interface MfaState {
  session: MfaSession;
  attempts: MfaAttempts;
  lockout: LockoutState;
}

export enum EMfaActionType {
  SET_SESSION = 'SET_SESSION',
  SET_LOCKOUT = 'SET_LOCKOUT',
  CLEAR_LOCKOUT = 'CLEAR_LOCKOUT',
  UPDATE_ATTEMPTS = 'UPDATE_ATTEMPTS',
  RESET_ATTEMPTS = 'RESET_ATTEMPTS',
  UPDATE_SESSION_TOKEN = 'UPDATE_SESSION_TOKEN'
}

export type MfaAction =
  | {
      type: EMfaActionType.SET_SESSION;
      payload: {
        email: string;
        password: string;
        rememberMe: boolean;
        mfaSessionToken: string;
        maxAttempts: number;
        currentAttempt: number;
      };
    }
  | {
      type: EMfaActionType.SET_LOCKOUT;
      payload: { endTime: Date; type: ELockoutType };
    }
  | { type: EMfaActionType.CLEAR_LOCKOUT }
  | { type: EMfaActionType.UPDATE_ATTEMPTS; payload: { current: number; max: number } }
  | { type: EMfaActionType.RESET_ATTEMPTS }
  | { type: EMfaActionType.UPDATE_SESSION_TOKEN; payload: string };
